/**
 * Sanitize database/service error messages for display to users.
 * Replaces raw constraint names, table names, UUIDs, and SQL errors
 * with friendly messages. Falls back to a generic message.
 *
 * Pattern entries support two shapes:
 *   { pattern, message }              — replace the raw message with `message`.
 *   { pattern, passThrough: true }    — surface the raw message verbatim.
 *
 * `passThrough` exists for cases where the upstream service (e.g. Stripe)
 * already produces user-facing copy that we *want* to show — translating it
 * to a generic message would remove actionable detail. The explicit flag
 * (rather than relying on the trailing "appears safe" branch) makes the
 * intent visible and prevents a future contributor from accidentally
 * adding a generic translation that swallows the useful message.
 */

const ERROR_PATTERNS = [
  // ── Card-decline pass-through (per card hsGzqWrI) ─────────────────────────
  // Stripe writes card-decline messages for cardholder consumption — they
  // explain the specific reason ("insufficient_funds", "expired_card") that
  // the user needs to know to recover. Pass them through verbatim.
  { pattern: /your card (was )?declined|card_declined|insufficient_funds|expired_card|incorrect_cvc|incorrect_number|invalid_(cvc|expiry|number)/i,
    passThrough: true },

  // ── Stripe-specific translations (per card hsGzqWrI) ──────────────────────
  // These match raw Stripe API error messages that edge functions pass
  // through verbatim. The "Stripe error: " prefix from update-subscription
  // doesn't need special handling — the regex matches the substring.
  { pattern: /no such customer/i,
    message: "Your subscription record couldn't be found. Please contact support so we can reconnect it." },
  { pattern: /no such subscription/i,
    message: "We couldn't find your active subscription. Please refresh the page or contact support." },
  { pattern: /no such (invoice|charge|payment_intent|setup_intent|price|product|plan)/i,
    message: 'A billing record is missing. Please contact support.' },
  { pattern: /resource_missing/i,
    message: "A billing record couldn't be found. Please refresh, or contact support if this persists." },
  { pattern: /authentication required|requires_action|requires_payment_method/i,
    message: 'Your card requires additional verification. Please complete the security check and try again.' },
  { pattern: /api key (is invalid|provided|expired)/i,
    message: 'Payment service is misconfigured. Please contact support.' },
  { pattern: /testmode_charges_only|livemode_mismatch/i,
    message: 'Payment service configuration mismatch. Please contact support.' },
  { pattern: /idempotency_key_in_use|already_used/i,
    message: 'This action was already processed. Please refresh and try again.' },

  // ── Database / generic patterns ───────────────────────────────────────────
  { pattern: /foreign key constraint/i, message: 'Something went wrong setting up your account. Please try again.' },
  { pattern: /user.?profile/i,          message: "We're setting up your account. Please wait a moment and try again." },
  { pattern: /unique constraint/i,      message: 'This record already exists. Please try again or contact support.' },
  { pattern: /statement.?timeout/i,     message: 'This is taking longer than expected. Please try again.' },
  { pattern: /canceling statement/i,    message: 'This is taking longer than expected. Please try again.' },
  { pattern: /not authenticated/i,      message: 'Your session has expired. Please refresh and try again.' },
  { pattern: /violates.*constraint/i,   message: 'Something went wrong. Please try again.' },
  { pattern: /edge function/i,          message: 'Something went wrong. Please try again.' },
  { pattern: /missing.*planTier|missing.*companyId/i, message: 'Something went wrong loading your plan. Please go back and try again.' },
  { pattern: /checkout failed/i,       message: 'Checkout could not be started. Please try again.' },
  { pattern: /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i, message: 'Something went wrong. Please try again.' },
  { pattern: /TypeError|ReferenceError|SyntaxError/i, message: 'Something went wrong. Please try again.' },
  { pattern: /Cannot read propert/i, message: 'Something went wrong. Please try again.' },
  { pattern: /failed to fetch|networkerror|net::ERR_/i, message: "We couldn't reach the server. Please check your connection and try again." },
  { pattern: /^\[[\w_]+\]\s/,          message: 'Something went wrong. Please try again or contact support.' },
  { pattern: /JSON\.parse|Unexpected token/i, message: 'Something went wrong. Please try again.' },
  { pattern: /CORS|blocked by|cross-origin/i, message: "We couldn't reach the server. Please try again." },
  { pattern: /quota exceeded|rate.?limit|429/i, message: 'This service is temporarily busy. Please wait a moment and try again.' },
  { pattern: /403|forbidden/i, message: "You don't have permission to perform this action." },
  { pattern: /404|not found/i, message: "The requested resource couldn't be found." },
  { pattern: /500|internal server/i, message: 'Something went wrong on our end. Please try again or contact support.' },
  { pattern: /\{.*"code"|"error"|"detail"/i, message: 'Something went wrong. Please try again.' },
  { pattern: /object Object/i, message: 'Something went wrong. Please try again.' },
];

const GENERIC_FALLBACK = 'Something went wrong. Please try again or contact support.';

export const sanitizeError = (rawMessage, fallback) => {
  if (!rawMessage) return fallback || GENERIC_FALLBACK;

  for (const { pattern, message, passThrough } of ERROR_PATTERNS) {
    if (pattern.test(rawMessage)) {
      return passThrough ? rawMessage : message;
    }
  }

  // If the message looks like a raw DB error (contains table/column names), use fallback
  if (/\b(public\.|pg_|_fkey|_pkey|_key|_idx|INSERT|UPDATE|DELETE|SELECT)\b/i.test(rawMessage)) {
    return fallback || GENERIC_FALLBACK;
  }

  // Message appears safe to show
  return rawMessage;
};
